import { CommonModule } from '@angular/common'
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Subscription, finalize } from 'rxjs'
import { ButtonModule } from 'primeng/button'
import { ProgressSpinnerModule } from 'primeng/progressspinner'
import { AffairsService } from '../../services/affairs.service'
import { UserService } from '../../../users/services/user.service'
import { ProductsService } from '../../../products/services/products.service'
import { InterventionsService } from '../../../interventions/services/interventions.service'
import { ToastService } from '../../../../shared/services/toast.service'
import { AffairScheduleInterventionCreateComponent } from '../affair-schedule-intervention-create/affair-schedule-intervention-create.component'
import { AffairScheduleInterventionScheduleComponent } from '../affair-schedule-intervention-schedule/affair-schedule-intervention-schedule.component'
import { UserModel } from '../../../../core/models/user.model'
import { AffairProductModel } from '../../../../core/models/affair-product.model'
import { environment } from '../../../../../environments/environment'

/**
 * Proposition de créneau issue de l'optimisation du vivier (AffairOptimizationSlot),
 * transmise dans l'URL par la vue vivier (`?proposals=…`), sur le même principe
 * que les propositions passées à la fiche Tablot.
 */
export interface ErosSlotProposal {
  date: string
  arrivalTime: string | null
  technicianId: number | null
  technicianName: string | null
  technicianEmail: string | null
  serviceDurationMin: number | null
}

/**
 * Page de validation d'une proposition de créneau pour une affaire EROS du vivier.
 *
 * Ouverte dans un nouvel onglet depuis la vue vivier (bouton « Programmer » des
 * affaires source eros). Parcours en 3 temps :
 *  1. choix d'une des propositions calculées par l'optimisation ;
 *  2. ajustement de l'horaire / plage horaire (même composant que l'étape
 *     « Programmation » du wizard : AffairScheduleInterventionScheduleComponent) ;
 *  3. informations de l'intervention (même composant que la dernière étape :
 *     AffairScheduleInterventionCreateComponent), puis création sur EROS.
 */
@Component({
  selector: 'app-affair-schedule-eros',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ProgressSpinnerModule,
    AffairScheduleInterventionScheduleComponent,
    AffairScheduleInterventionCreateComponent,
  ],
  templateUrl: './affair-schedule-eros.component.html',
  styleUrls: ['./affair-schedule-eros.component.scss'],
})
export class AffairScheduleErosComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute)
  private router = inject(Router)
  private fb = inject(FormBuilder)
  private affairsService = inject(AffairsService)
  private usersService = inject(UserService)
  private productsService = inject(ProductsService)
  private interventionsService = inject(InterventionsService)
  private toastService = inject(ToastService)

  @ViewChild(AffairScheduleInterventionCreateComponent)
  interventionCreateComponent?: AffairScheduleInterventionCreateComponent

  @ViewChild(AffairScheduleInterventionScheduleComponent)
  interventionScheduleComponent?: AffairScheduleInterventionScheduleComponent

  public mlRef: string = this.route.snapshot.params['mlRef']
  public affairId: number | null = null

  /** Fenêtre de planification souhaitée (bornes incluses), transmise par EROS. */
  public wishedPlanningStartDate: string | null = null
  public wishedPlanningEndDate: string | null = null

  public affair: any = null
  public affairLoading = true

  public proposals: ErosSlotProposal[] = []
  public selectedProposalIndex: number | null = null

  /** Utilisateurs complets (avec erpSource) pour résoudre le technicien. */
  public users: UserModel[] = []
  public usersLoading = true

  /** Liste allégée { label, value } pour les selects du composant horaire. */
  public usersForSelect: { label: string; value: number }[] = []

  public schedulableProducts: AffairProductModel[] = []
  public productsLoading = true

  /** Formulaires et inputs partagés avec les composants du wizard. */
  public scheduleForm: FormGroup
  public interventionForm: FormGroup
  public selectedTechnicians: UserModel[] = []
  public scheduleData: any = null
  public droppedDetails: any = null
  public techniciansIncompatible = false

  public submittingIntervention = false
  public interventionError = ''
  public interventionFormLoading = false

  private subscriptions: Subscription[] = []

  ngOnInit(): void {
    this.initForms()
    this.parseQueryParams()

    this.subscriptions.push(
      this.affairsService
        .getAffairById(this.mlRef)
        .pipe(
          // finalize s'exécute APRÈS next : c'est ici qu'on peut baisser le flag
          // de chargement puis tenter d'appliquer la proposition pré-sélectionnée.
          finalize(() => {
            this.affairLoading = false
            this.tryApplySelectedProposal()
          })
        )
        .subscribe({
          next: (affair) => {
            this.affair = affair
          },
          error: () => {
            this.interventionError = `Impossible de charger l'affaire ${this.mlRef}`
          },
        })
    )

    // Utilisateurs complets (avec erpSource) pour résoudre le technicien de la proposition
    this.subscriptions.push(
      this.usersService
        .getUsersWithFilters({
          departmentsCodes: [],
          societyKeys: [],
          search: '',
          productsNames: [],
        })
        .pipe(
          finalize(() => {
            this.usersLoading = false
            this.tryApplySelectedProposal()
          })
        )
        .subscribe({
          next: (users) => {
            this.users = users ?? []
          },
          error: () => {
            this.interventionError =
              'Impossible de charger la liste des techniciens'
          },
        })
    )

    // Liste allégée pour les selects du composant horaire (technicien / assistant)
    this.subscriptions.push(
      this.usersService.getForSelect().subscribe({
        next: (users) => (this.usersForSelect = users ?? []),
        error: () => {},
      })
    )

    // Produits restant à programmer sur l'affaire (source de productsData,
    // comme la sélection de produits du wizard)
    this.subscriptions.push(
      this.productsService
        .getSchedulableProductsByMlRef(this.mlRef)
        .pipe(
          finalize(() => {
            this.productsLoading = false
            this.tryApplySelectedProposal()
          })
        )
        .subscribe({
          next: (products) => {
            this.schedulableProducts = products ?? []
          },
          error: () => {
            this.interventionError =
              "Impossible de charger les produits de l'affaire"
          },
        })
    )
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe())
  }

  private initForms(): void {
    // Mêmes structures que affair-schedule-steps (initScheduleForm / initInterventionForm)
    this.scheduleForm = this.fb.group({
      startDate: [new Date(), Validators.required],
      duration: [null],
      isTimeSlot: [false],
      startTimeSlot: [null],
      endTimeSlot: [null],
      techniciansIds: [[]],
      userAssistantId: [null],
      isConfirmed: [true],
      dateRange: [[]],
    })

    this.subscriptions.push(
      this.scheduleForm.valueChanges.subscribe((formData) => {
        this.scheduleData = formData
      })
    )

    this.interventionForm = this.fb.group({
      mlRef: [this.mlRef],
      name: ['', Validators.required],
      generatedDescription: ['', Validators.required],
      description: '',
      conformityId: null,
      worksSupervisorId: null,
      isDeliveredHouse: false,
      isWaitingCheck: false,
      recoverCheck: false,
      isOffice: false,
      doNotShift: false,
      doNotShiftReason: '',
      contacts: null,
      affairsContacts: null,
      workSupervisorId: null,
      erpSource: null,
      affairProducts: [[]],
      productData: null,
    })
  }

  private parseQueryParams(): void {
    const qp = this.route.snapshot.queryParams

    const affairId = Number(qp['affairId'])
    this.affairId = Number.isFinite(affairId) && affairId > 0 ? affairId : null

    this.wishedPlanningStartDate = qp['wishedStart'] || null
    this.wishedPlanningEndDate = qp['wishedEnd'] || null

    try {
      const raw = qp['proposals']
      const parsed = raw ? JSON.parse(raw) : []
      if (Array.isArray(parsed)) {
        this.proposals = parsed
          .filter((p): p is ErosSlotProposal => !!p && !!p.date)
          .slice(0, 10)
      }
    } catch {
      this.proposals = []
    }

    // Pré-sélectionner la première proposition : la vue vivier ordonne les
    // propositions avec le créneau de la tournée cliquée (« Programmer ») en tête.
    if (this.proposals.length > 0) {
      this.selectedProposalIndex = 0
    }
  }

  get loading(): boolean {
    return this.affairLoading || this.usersLoading || this.productsLoading
  }

  get selectedProposal(): ErosSlotProposal | null {
    return this.selectedProposalIndex != null
      ? (this.proposals[this.selectedProposalIndex] ?? null)
      : null
  }

  /** Les étapes 2 et 3 ne sont affichées qu'une fois une proposition validée. */
  get canShowForms(): boolean {
    return (
      !this.loading &&
      !!this.affair &&
      !!this.selectedProposal &&
      this.selectedTechnicians.length > 0
    )
  }

  /**
   * Le bouton reste cliquable pendant la génération nom/description :
   * la validité réelle du formulaire est vérifiée au clic (isFormValid exige
   * nom + description remplis). Ne pas griser sur `interventionFormLoading` :
   * ce signal, émis en continu par le composant du wizard via
   * ngAfterContentChecked, peut rester bloqué à true selon le cycle de
   * change detection et rendre le bouton définitivement inactif.
   */
  get canSubmit(): boolean {
    return (
      this.canShowForms &&
      !this.techniciansIncompatible &&
      !this.submittingIntervention
    )
  }

  selectProposal(index: number): void {
    this.selectedProposalIndex = index
    this.interventionError = ''
    this.tryApplySelectedProposal()
  }

  /**
   * Applique la proposition sélectionnée dès que toutes les données sont
   * chargées : résout le technicien (→ erpSource), pré-remplit le formulaire
   * horaire et construit droppedDetails (productsData) attendus par les
   * composants du wizard.
   */
  private tryApplySelectedProposal(): void {
    const proposal = this.selectedProposal
    if (!proposal || this.loading || !this.affair) return

    const technician = this.resolveTechnician(proposal)
    if (!technician) {
      this.interventionError = `Technicien introuvable pour la proposition sélectionnée (${proposal.technicianName ?? proposal.technicianEmail ?? '?'})`
      this.selectedTechnicians = []
      return
    }

    this.selectedTechnicians = [technician]

    const erpSource = this.resolveErpSource(technician.erpSource || 'mylink')
    this.interventionForm.patchValue({ erpSource })

    const startDate = this.buildStartDate(proposal)
    const durationMinutes = this.computeDurationMinutes(proposal)

    const duration = new Date()
    duration.setHours(Math.floor(durationMinutes / 60))
    duration.setMinutes(durationMinutes % 60)
    duration.setSeconds(0)

    // Pré-remplissage du formulaire horaire : l'utilisateur reste libre
    // d'ajuster l'heure ou de passer en plage horaire (composant étape 4).
    this.scheduleForm.patchValue({
      startDate,
      duration,
      techniciansIds: [technician.id],
    })
    this.scheduleData = this.scheduleForm.value

    // Même contrat que le drop du calendrier du wizard : productsData alimente
    // la génération nom/description et les affairProducts du formulaire final.
    this.droppedDetails = {
      date: startDate,
      duration: durationMinutes,
      productsData: this.schedulableProducts,
    }
  }

  private resolveTechnician(proposal: ErosSlotProposal): UserModel | null {
    const byId =
      proposal.technicianId != null
        ? this.users.find((u) => u.id === proposal.technicianId)
        : null
    if (byId) return byId

    const email = proposal.technicianEmail?.toLowerCase()
    if (!email) return null
    return (
      this.users.find((u) => (u.email ?? '').toLowerCase() === email) ?? null
    )
  }

  /**
   * Construit la date/heure de début en heure « métier ».
   *
   * IMPORTANT — fuseau : le backend encode l'heure métier dans le timestamp UTC
   * (convention de la vue vivier, qui affiche avec `timeZone: 'UTC'`). On doit
   * donc lire les composantes UTC de l'ISO et construire une Date LOCALE avec
   * ces valeurs, sinon l'heure affichée/envoyée serait décalée du fuseau du
   * navigateur (ex: +2h à Paris en été).
   */
  private buildStartDate(proposal: ErosSlotProposal): Date {
    if (proposal.arrivalTime) {
      const d = new Date(proposal.arrivalTime)
      if (!Number.isNaN(d.getTime())) {
        return new Date(
          d.getUTCFullYear(),
          d.getUTCMonth(),
          d.getUTCDate(),
          d.getUTCHours(),
          d.getUTCMinutes(),
          0
        )
      }
    }
    return new Date(`${proposal.date}T08:00:00`)
  }

  private computeDurationMinutes(proposal: ErosSlotProposal): number {
    const fromProducts = this.schedulableProducts.reduce(
      (sum, p) => sum + (p.product?.duration || 0),
      0
    )
    if (fromProducts > 0) return fromProducts
    if (proposal.serviceDurationMin && proposal.serviceDurationMin > 0) {
      return Math.round(proposal.serviceDurationMin)
    }
    return 120
  }

  onScheduleChanged(scheduleData: any): void {
    this.scheduleData = scheduleData
  }

  onSubmitIntervention(): void {
    if (!this.interventionCreateComponent) return

    if (
      this.interventionScheduleComponent &&
      !this.interventionScheduleComponent.isFormValid()
    ) {
      this.interventionError =
        'Veuillez remplir correctement les informations de programmation'
      return
    }

    if (!this.interventionCreateComponent.isFormValid()) {
      this.interventionError = 'Veuillez remplir tous les champs obligatoires'
      return
    }

    // S'assurer que les dernières valeurs du formulaire horaire sont utilisées
    if (this.interventionScheduleComponent) {
      this.scheduleData = this.interventionScheduleComponent.getFormData()
    }

    this.submittingIntervention = true
    this.interventionError = ''

    this.interventionCreateComponent
      .submitForm()
      .then((response: any) => {
        const erpSource =
          response.data?.usersTechnicians?.[0]?.erpSource ||
          this.interventionForm.get('erpSource')?.value ||
          'mylink'

        const interventionId = response.data?.id

        // Ouvrir l'affaire sur l'ERP EROS correspondant, dans un nouvel onglet
        this.openAffairOnEros(erpSource)

        this.toastService.showSuccess("L'intervention a été créée avec succès")

        this.router.navigate([
          '/intervention',
          interventionId,
          'view',
          erpSource,
        ])
      })
      .catch((err: any) => {
        console.error('Error creating intervention:', err)
        this.interventionError =
          typeof err === 'string'
            ? err
            : err.error?.message ||
              "Une erreur est survenue lors de la création de l'intervention"
      })
      .finally(() => {
        this.submittingIntervention = false
      })
  }

  onInterventionFormStateChanged(event: {
    valid: boolean
    data: any
    isLoading?: boolean
  }): void {
    this.interventionFormLoading = event.isLoading ?? false
  }

  onInterventionFormError(error: string): void {
    this.interventionError = error
  }

  /** Ouvre l'affaire sur l'app EROS correspondante (même logique que le wizard). */
  private openAffairOnEros(erpSource: string): void {
    if (!this.affair) return
    const affairId = this.affair.srcErp?.[erpSource]?.id
    if (!affairId) return

    if (erpSource === 'abm') {
      window.open(
        environment.abmUrl + 'affairs/' + affairId + '/show',
        '_blank'
      )
    } else if (erpSource === 'nrj') {
      window.open(
        environment.nrjUrl + 'affairs/' + affairId + '/show',
        '_blank'
      )
    }
  }

  /**
   * Résout l'erpSource en forçant 'nrj' si l'affaire est de type inspection
   * (marketTypeId === 5) — même règle que affair-schedule-steps.
   */
  private resolveErpSource(defaultSource: string): string {
    if (this.affair?.marketType?.id === 5) {
      return 'nrj'
    }
    return defaultSource
  }

  formatProposalDate(proposal: ErosSlotProposal): string {
    const date = new Date(`${proposal.date}T00:00:00`)
    if (Number.isNaN(date.getTime())) return proposal.date
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
  }

  formatProposalTime(proposal: ErosSlotProposal): string {
    if (!proposal.arrivalTime) return ''
    const d = new Date(proposal.arrivalTime)
    if (Number.isNaN(d.getTime())) return ''
    // Heure « métier » encodée en UTC par le backend — même convention
    // d'affichage que la vue vivier (formatTime avec timeZone: 'UTC').
    return d.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    })
  }

  get hasWishedWindow(): boolean {
    return !!this.wishedPlanningStartDate || !!this.wishedPlanningEndDate
  }

  /** Libellé lisible de la fenêtre souhaitée (ex: « du 15 juil. au 30 juil. 2026 »). */
  get wishedWindowLabel(): string {
    const fmt = (dateStr: string) =>
      new Date(`${dateStr}T00:00:00`).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })

    const start = this.wishedPlanningStartDate
    const end = this.wishedPlanningEndDate

    if (start && end) return `du ${fmt(start)} au ${fmt(end)}`
    if (start) return `à partir du ${fmt(start)}`
    if (end) return `jusqu'au ${fmt(end)}`
    return ''
  }

  /**
   * Vrai si la date actuellement retenue (formulaire horaire, sinon proposition
   * sélectionnée) sort de la fenêtre souhaitée — affiche une alerte visible.
   */
  get isOutsideWishedWindow(): boolean {
    if (!this.hasWishedWindow) return false

    const startDate: Date | null =
      this.scheduleForm?.get('startDate')?.value ??
      (this.selectedProposal ? this.buildStartDate(this.selectedProposal) : null)
    if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) {
      return false
    }

    const pad = (n: number) => String(n).padStart(2, '0')
    const day = `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())}`

    if (this.wishedPlanningStartDate && day < this.wishedPlanningStartDate) {
      return true
    }
    return !!this.wishedPlanningEndDate && day > this.wishedPlanningEndDate
  }

  technicianInitials(name: string | null): string {
    if (!name) return '?'
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]!.toUpperCase())
      .join('')
  }
}
